import type { Hex } from "viem";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { BuildTxSuccessPayload } from "../types.js";

import {
  getInjectedProvider,
  publicClientFor,
  switchChain,
  walletClientFor,
  type WalletState,
} from "../lib/wallet.js";

/**
 * Owns the sign + broadcast + confirm lifecycle of each step in a
 * `BuildTxSuccess` payload. Extracted from `StepsExecutor` so the UI
 * component is render-only (audit v7 had Cog 37 — well into the
 * critical band).
 *
 * Returned `statuses` is a parallel array to `result.steps`; index
 * stable, never reordered. `signStep(i)` is idempotent against
 * confirmed/broadcast states (callers can wire the button without
 * extra guards — `isUnlocked(i)` does the gating).
 *
 * Receipt polling: `waitForTransactionReceipt` with timeout 120 s +
 * retryCount 3 (vs default 60 s / 1) absorbs wallet-provider RPC lag.
 * On timeout we do a one-shot `getTransactionReceipt` fallback because
 * "wait timed out" doesn't mean "tx failed" — usually means our RPC
 * node hasn't propagated the receipt yet. If THAT also misses, the UI
 * surfaces an error that hints the tx may still be mined.
 */

export type StepStatus =
  | { readonly kind: "pending" }
  | { readonly kind: "signing" }
  | { readonly kind: "broadcast"; readonly hash: Hex }
  | {
      readonly kind: "confirmed";
      readonly hash: Hex;
      readonly blockNumber: bigint;
    }
  | { readonly kind: "error"; readonly message: string; readonly hash?: Hex };

export interface UseStepRunnerArgs {
  readonly result: BuildTxSuccessPayload;
  readonly wallet: WalletState;
  /** Called once every step transitions to `confirmed`. */
  readonly onCompleted?: (hashes: readonly Hex[]) => void;
}

export interface UseStepRunnerState {
  readonly statuses: readonly StepStatus[];
  readonly isStepUnlocked: (index: number) => boolean;
  readonly signStep: (index: number) => Promise<void>;
  readonly allConfirmed: boolean;
  readonly confirmedCount: number;
}

const RECEIPT_WAIT_TIMEOUT_MS = 120_000;
const RECEIPT_WAIT_RETRY_COUNT = 3;

function describeErr(err: unknown): string {
  if (err === undefined || err === null) return "Unknown error";
  if (typeof err === "string") return err;
  if (typeof err === "object") {
    const candidate = err as { shortMessage?: unknown; message?: unknown };
    if (typeof candidate.shortMessage === "string")
      return candidate.shortMessage;
    if (typeof candidate.message === "string") return candidate.message;
  }
  return String(err);
}

export function useStepRunner(args: UseStepRunnerArgs): UseStepRunnerState {
  const { result, wallet, onCompleted } = args;
  const [statuses, setStatuses] = useState<readonly StepStatus[]>(() =>
    result.steps.map(() => ({ kind: "pending" }) as StepStatus),
  );
  const abortRef = useRef<{ aborted: boolean }>({ aborted: false });
  const onCompletedFiredRef = useRef(false);

  // Reset on a fresh result (the user re-quoted). State writes here are
  // deliberate: this is the documented "sync prop into state" pattern.
  useEffect(() => {
    const abort = abortRef.current;
    abort.aborted = false;
    onCompletedFiredRef.current = false;
    // eslint-disable-next-line @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect
    setStatuses(result.steps.map(() => ({ kind: "pending" }) as StepStatus));
    return () => {
      abort.aborted = true;
    };
  }, [result.steps]);

  const allConfirmed = useMemo(
    () => statuses.every((s) => s.kind === "confirmed"),
    [statuses],
  );
  const confirmedCount = useMemo(
    () => statuses.filter((s) => s.kind === "confirmed").length,
    [statuses],
  );

  useEffect(() => {
    if (!allConfirmed || onCompletedFiredRef.current) return;
    const hashes = statuses
      .filter(
        (
          s,
        ): s is {
          readonly kind: "confirmed";
          readonly hash: Hex;
          readonly blockNumber: bigint;
        } => s.kind === "confirmed",
      )
      .map((s) => s.hash);
    onCompletedFiredRef.current = true;
    onCompleted?.(hashes);
  }, [allConfirmed, statuses, onCompleted]);

  const updateStatus = useCallback((index: number, next: StepStatus) => {
    setStatuses((prev) => prev.map((s, i) => (i === index ? next : s)));
  }, []);

  const isStepUnlocked = useCallback(
    (index: number): boolean => {
      const current = statuses[index];
      if (current === undefined) return false;
      const isResumable =
        current.kind === "pending" || current.kind === "error";
      if (index === 0) return isResumable;
      const allPriorConfirmed = statuses
        .slice(0, index)
        .every((s) => s.kind === "confirmed");
      return allPriorConfirmed && isResumable;
    },
    [statuses],
  );

  const broadcastStep = useCallback(
    async (
      index: number,
    ): Promise<{ readonly hash: Hex } | { readonly error: string }> => {
      const provider = getInjectedProvider();
      if (provider === undefined) return { error: "No injected wallet" };
      const step = result.steps[index]!;

      if (step.tx.chainId !== wallet.chainId) {
        try {
          await switchChain(provider, step.tx.chainId);
        } catch (err) {
          return { error: `Chain switch failed: ${describeErr(err)}` };
        }
      }

      updateStatus(index, { kind: "signing" });

      try {
        const client = walletClientFor(
          provider,
          step.tx.chainId,
          wallet.address,
        );
        const hash = await client.sendTransaction({
          account: wallet.address,
          chain: undefined,
          to: step.tx.to as Hex,
          data: step.tx.data as Hex,
          // GraphQL returns `null` for optional numeric fields; treat
          // null/undefined alike as "not provided" so BigInt() doesn't throw.
          value: step.tx.value == null ? 0n : BigInt(step.tx.value),
          ...(step.tx.gasLimit != null
            ? { gas: BigInt(step.tx.gasLimit) }
            : {}),
        });
        return { hash };
      } catch (err) {
        return { error: describeErr(err) };
      }
    },
    [result.steps, wallet, updateStatus],
  );

  const waitForReceipt = useCallback(
    async (index: number, hash: Hex): Promise<void> => {
      const step = result.steps[index]!;
      try {
        const publicClient = publicClientFor(
          getInjectedProvider()!,
          step.tx.chainId,
        );
        const receipt = await publicClient.waitForTransactionReceipt({
          hash,
          timeout: RECEIPT_WAIT_TIMEOUT_MS,
          retryCount: RECEIPT_WAIT_RETRY_COUNT,
          confirmations: 1,
        });
        if (abortRef.current.aborted) return;
        if (receipt.status === "reverted") {
          updateStatus(index, {
            kind: "error",
            message: `Reverted in block ${receipt.blockNumber.toString()}`,
            hash,
          });
          return;
        }
        updateStatus(index, {
          kind: "confirmed",
          hash,
          blockNumber: receipt.blockNumber,
        });
      } catch (err) {
        if (abortRef.current.aborted) return;
        // Timeout doesn't mean failure — RPC node lag. Try a one-shot
        // direct read before declaring error.
        try {
          const publicClient = publicClientFor(
            getInjectedProvider()!,
            step.tx.chainId,
          );
          const receipt = await publicClient.getTransactionReceipt({ hash });
          if (abortRef.current.aborted) return;
          if (receipt.status === "success") {
            updateStatus(index, {
              kind: "confirmed",
              hash,
              blockNumber: receipt.blockNumber,
            });
            return;
          }
          updateStatus(index, {
            kind: "error",
            message: `Reverted in block ${receipt.blockNumber.toString()}`,
            hash,
          });
          return;
        } catch {
          // Receipt truly absent — fall through.
        }
        updateStatus(index, {
          kind: "error",
          message: `Receipt poll failed: ${describeErr(err)} (la tx puede estar minada — comprueba el explorer)`,
          hash,
        });
      }
    },
    [result.steps, updateStatus],
  );

  const signStep = useCallback(
    async (index: number): Promise<void> => {
      const broadcast = await broadcastStep(index);
      if ("error" in broadcast) {
        updateStatus(index, { kind: "error", message: broadcast.error });
        return;
      }
      updateStatus(index, { kind: "broadcast", hash: broadcast.hash });
      await waitForReceipt(index, broadcast.hash);
    },
    [broadcastStep, waitForReceipt, updateStatus],
  );

  return { statuses, isStepUnlocked, signStep, allConfirmed, confirmedCount };
}
